import { useEffect, useState } from "react";

import { api } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import type { PolicySigningStatus } from "../../../lib/policySigning.js";
import { SignedPolicyDownloads } from "../../PolicySignPanel.js";
import { Badge } from "../../ui/badge.js";
import { CollapsibleSection } from "../sectionShared.js";
import { PolicyAssignmentsPanel } from "./PolicyAssignmentsPanel.js";

// Profile → Policy Documents, on every employee. HR chooses which documents
// this employee must sign and downloads every signed copy; the employee sees
// what is waiting for them (they sign it in the login pop-up) and downloads
// their own signed copies.
export function PolicyDocumentsSection({ sectionRef, employeeId, isHR, readOnly }: {
  sectionRef: React.RefObject<HTMLDivElement>;
  employeeId: string | null;
  isHR: boolean;
  readOnly: boolean;
}) {
  if (!employeeId) return null;
  return (
    // The employee's own view has nothing to edit, only downloads.
    <CollapsibleSection id="policies" sectionRef={sectionRef} title="Policy Documents" editableWhenReadOnly={!isHR}>
      {isHR ? <PolicyAssignmentsPanel employeeId={employeeId} readOnly={readOnly} /> : <OwnPolicyDocuments employeeId={employeeId} />}
    </CollapsibleSection>
  );
}

function OwnPolicyDocuments({ employeeId }: { employeeId: string }) {
  const [status, setStatus] = useState<PolicySigningStatus | null>(null);

  useEffect(() => {
    api.get<PolicySigningStatus>(`/employees/${employeeId}/handbook/status`)
      .then(r => setStatus(r.data))
      .catch((err) => { logError("PolicyDocumentsSection.load", err, { employeeId }); });
  }, [employeeId]);

  if (!status) return <p className="text-xs text-surface-400">Loading…</p>;
  return (
    <div className="space-y-3">
      {status.outstanding.length > 0 && (
        <ul className="space-y-1.5">
          {status.outstanding.map(d => (
            <li key={d.kind} className="flex flex-wrap items-center gap-2 text-[13px]">
              <span className="min-w-0 flex-1">{d.title} v{d.version}</span>
              <Badge tone="warning">Waiting for your signature</Badge>
            </li>
          ))}
        </ul>
      )}
      {status.signed.length > 0
        ? <SignedPolicyDownloads signed={status.signed} />
        : status.outstanding.length === 0 && <p className="text-xs text-surface-500">No signed documents yet.</p>}
    </div>
  );
}
